// HTTP client for the pinmerge server API. Replaces direct Supabase access — the
// widget bundle no longer carries any Supabase credentials. The server validates the
// widget_token, scopes the URL against allowed_urls, and writes via service-role.

import type { ClickOffsetPct, ClickXY, ElementRect, Viewport } from '@pinmerge/shared';

export interface SubmitArgs {
  token: string;
  creatorToken: string;
  url: string;
  page_title: string | null;
  comment_text: string;
  author_name: string;
  viewport: Viewport;
  click_xy: ClickXY | null;
  click_offset_pct: ClickOffsetPct | null;
  element_rect: ElementRect | null;
  element_selector: string | null;
  element_html: string | null;
  /** PNG Blob to be base64-encoded and sent with the comment. Optional. */
  screenshot?: Blob | null;
  /** Cropped element-area PNG. Optional — falls back to no thumbnail at view time. */
  thumbnail?: Blob | null;
  /** When set, this is a reply. Server validates the parent and overrides
   *  page_url/viewport from the parent row; pin-position fields ignored. */
  parent_id?: string | null;
  /** Prototype-version slug from ?pinmerge_v=. Server resolves it to a
   *  prototype_versions.id and tags the comment. NULL = server falls back to
   *  the project's default version (if any). */
  version_slug?: string | null;
  /** Opaque route bucket from an optional HostAdapter (= RouteInfo.pattern).
   *  Stored verbatim as comments.page_key and used as the read-time bucket.
   *  Null (script-tag channel / null-adapter) → server falls back to
   *  pageKeyOf(url), i.e. today's behaviour (I5). */
  page_key?: string | null;
  /** Route + source anchor from an optional HostAdapter (WP-1.x/3.1). All
   *  optional. Field names map 1:1 to the DB columns. */
  route_pattern?: string | null;
  route_params?: Record<string, string> | null;
  source_file?: string | null;
  source_line?: number | null;
  source_column?: number | null;
  component_name?: string | null;
}

export interface SubmitResult {
  id: string;
  screenshot_path: string | null;
  thumbnail_path: string | null;
  /** Signed URL for the thumbnail, returned alongside the path so the widget
   *  can render the screenshot in the freshly-created pin without waiting for
   *  the next GET list. Null when no thumbnail was uploaded. */
  thumbnail_url: string | null;
  /** Author's avatar so the freshly-created pin can render the right face
   *  without waiting for the next GET list. Null if reviewer lookup failed. */
  reviewer: { avatar_id: string | null; avatar_bg: string | null } | null;
}

export interface RemoteReaction {
  emoji: string;
  count: number;
  /** True if the requesting reviewer (resolved via creator_token) is among the reactors. */
  mine: boolean;
  /** Chronological reactor names from the server (capped at 50). The widget
   *  builds the tooltip from these, swapping the viewer's own name for `Du`. */
  reactor_names: string[];
}

export interface RemoteReviewerStub {
  avatar_id: string | null;
  avatar_bg: string | null;
}

export interface RemoteComment {
  id: string;
  page_url: string;
  page_title: string | null;
  viewport: Viewport;
  element_rect: ElementRect | null;
  click_xy: ClickXY | null;
  click_offset_pct: ClickOffsetPct | null;
  screenshot_path: string | null;
  thumbnail_path: string | null;
  /** Signed URL to the cropped element-area thumbnail. Server-resolved at list
   *  time so the widget doesn't need a separate sign-URL endpoint. */
  thumbnail_url: string | null;
  comment_text: string;
  author_name: string;
  element_html: string | null;
  element_selector: string | null;
  status: 'open' | 'in_progress' | 'resolved';
  /** Server-computed ownership flag: true when this pin's creator_token matches
   *  the caller's. The raw creator_token is never sent — it's a bearer
   *  credential that would let any reader edit/delete others' comments. */
  mine: boolean;
  reviewer_id: string | null;
  reviewer: RemoteReviewerStub | null;
  reactions: RemoteReaction[];
  parent_id: string | null;
  /** Prototype-version this comment was tagged under, when one is set. NULL when
   *  the project has no versions (or the project pre-dates the feature). */
  prototype_version: { id: string; slug: string; label: string } | null;
  /** Route + source anchor from an optional HostAdapter (WP-1.x). All optional
   *  and not yet emitted by the server. Field names map 1:1 to the DB columns. */
  route_pattern?: string | null;
  route_params?: Record<string, string> | null;
  source_file?: string | null;
  source_line?: number | null;
  source_column?: number | null;
  component_name?: string | null;
  created_at: string;
  updated_at: string;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, public detail?: string) {
    super(detail ? `api error ${status}: ${code} (${detail})` : `api error ${status}: ${code}`);
  }
}

export async function submitComment(apiUrl: string, args: SubmitArgs): Promise<SubmitResult> {
  const screenshotBase64 = args.screenshot ? await blobToBase64(args.screenshot) : null;
  const thumbnailBase64 = args.thumbnail ? await blobToBase64(args.thumbnail) : null;

  const res = await fetch(`${apiUrl}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
      page_title: args.page_title,
      comment_text: args.comment_text,
      author_name: args.author_name,
      viewport: args.viewport,
      click_xy: args.click_xy,
      click_offset_pct: args.click_offset_pct,
      element_rect: args.element_rect,
      element_selector: args.element_selector,
      element_html: args.element_html,
      screenshot_base64: screenshotBase64,
      thumbnail_base64: thumbnailBase64,
      parent_id: args.parent_id ?? null,
      version_slug: args.version_slug ?? null,
      // Route + source anchoring (WP-3.1). All null unless a HostAdapter
      // supplied values — a null-adapter leaves every field null, so the POST
      // body is byte-equivalent to the script-tag channel's (I3). The server
      // never recomputes page_key; null → it falls back to pageKeyOf(url) (I5).
      page_key: args.page_key ?? null,
      route_pattern: args.route_pattern ?? null,
      route_params: args.route_params ?? null,
      source_file: args.source_file ?? null,
      source_line: args.source_line ?? null,
      source_column: args.source_column ?? null,
      component_name: args.component_name ?? null,
    }),
  });

  if (!res.ok) throw await asError(res);
  return (await res.json()) as SubmitResult;
}

export interface ListResult {
  comments: RemoteComment[];
  /** Project metadata exposed via the GET response so the widget can build
   *  permalinks and (later) chrome titles without an extra round trip. */
  project: { slug: string };
  /** Resolved prototype-version metadata for the current `?pinmerge_v=` (or
   *  the project's default version if no param was sent). NULL when the
   *  project has no versions configured. The widget uses this for the
   *  badge in the FAB toolbar. */
  active_version: { id: string; slug: string; label: string } | null;
}

export async function listComments(
  apiUrl: string,
  args: {
    token: string;
    url: string;
    creatorToken: string;
    versionSlug?: string | null;
    /** Route bucket from an optional HostAdapter (= RouteInfo.pattern). When
     *  set, the server filters on this bucket instead of pageKeyOf(url). Absent
     *  / null (script-tag channel / null-adapter) → URL fallback, unchanged. */
    pageKey?: string | null;
  },
): Promise<ListResult> {
  const qs = new URLSearchParams({
    token: args.token,
    url: args.url,
    creator_token: args.creatorToken,
  });
  if (args.versionSlug) qs.set('version', args.versionSlug);
  if (args.pageKey) qs.set('page_key', args.pageKey);
  const res = await fetch(`${apiUrl}/comments?${qs}`, { method: 'GET' });
  if (!res.ok) throw await asError(res);
  return (await res.json()) as ListResult;
}

export async function toggleReaction(
  apiUrl: string,
  args: { id: string; token: string; creatorToken: string; url: string; emoji: string },
): Promise<'added' | 'changed' | 'removed'> {
  const res = await fetch(`${apiUrl}/comments/${args.id}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
      emoji: args.emoji,
    }),
  });
  if (!res.ok) throw await asError(res);
  const body = (await res.json()) as { result: 'added' | 'changed' | 'removed' };
  return body.result;
}

export async function updateComment(
  apiUrl: string,
  args: {
    id: string;
    token: string;
    creatorToken: string;
    url: string;
    comment_text: string;
  },
): Promise<void> {
  const res = await fetch(`${apiUrl}/comments/${args.id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
      comment_text: args.comment_text,
    }),
  });
  if (!res.ok) throw await asError(res);
}

export async function deleteComment(
  apiUrl: string,
  args: { id: string; token: string; creatorToken: string; url: string },
): Promise<void> {
  const res = await fetch(`${apiUrl}/comments/${args.id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
    }),
  });
  if (!res.ok) throw await asError(res);
}

export interface HandoffReviewer {
  id: string;
  email: string;
  name: string | null;
  avatar_id: string | null;
  avatar_bg: string | null;
}

export async function exchangeUrlHandoff(
  apiUrl: string,
  args: { token: string; creatorToken: string; url: string; jwt: string },
): Promise<HandoffReviewer> {
  const res = await fetch(`${apiUrl}/auth/handoff/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
      jwt: args.jwt,
    }),
  });
  if (!res.ok) throw await asError(res);
  const body = (await res.json()) as { ok: boolean; reviewer: HandoffReviewer };
  return body.reviewer;
}

export async function exchangeIframeHandoff(
  apiUrl: string,
  args: { token: string; creatorToken: string; url: string; jwt: string },
): Promise<HandoffReviewer> {
  const res = await fetch(`${apiUrl}/auth/handoff/iframe-exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      creator_token: args.creatorToken,
      url: args.url,
      jwt: args.jwt,
    }),
  });
  if (!res.ok) throw await asError(res);
  const body = (await res.json()) as { ok: boolean; reviewer: HandoffReviewer };
  return body.reviewer;
}

async function asError(res: Response): Promise<ApiError> {
  const data = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
  return new ApiError(res.status, data.error ?? 'unknown', data.detail);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Chunked conversion — String.fromCharCode.apply chokes on huge arrays.
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}
